/**
 * Pure-function logic for To-Do permission resolution.
 * Mirrors the edit/delete matrix documented in §3.9 and §7 of
 * FUNCTIONAL_DOCUMENTATION.md and the logic inside Todo.tsx.
 */

import {
  SecurityRolePermission,
  hasModuleViewPermission,
  hasModuleEditPermission,
  hasModuleDeletePermission,
  hasModuleExecutePermission,
} from 'core/services/permissionService';

export interface ToDoPermissions {
  /** User can see the To-Do module (My and/or All). */
  canView: boolean;
  /** User can see the My Outstanding section and my-scoped buckets. */
  canViewMy: boolean;
  /** User can see to-dos across the whole workplace (not just assigned-to-self). */
  canViewAll: boolean;
  /** User can create/edit to-do items, respecting My vs All scope. */
  canEdit: boolean;
  /** User can delete to-do items, respecting My vs All scope. */
  canDelete: boolean;
  /** User can mark to-dos as complete. */
  canComplete: boolean;
}

/**
 * Derives the full To-Do permission set from a SecurityRolePermission array.
 *
 * Rule (from Todo.tsx):
 *   if user has "To-Do - All":
 *     canEdit   = edit on (All  OR  My)
 *     canDelete = delete on (All  OR  My)
 *   else if user has only "To-Do - My":
 *     canEdit   = edit on My
 *     canDelete = delete on My
 */
export function resolveToDoPermissions(
  perms: SecurityRolePermission[],
): ToDoPermissions {
  const hasAll = hasModuleViewPermission(perms, 'To-Do - All');
  const hasMy  = hasModuleViewPermission(perms, 'To-Do - My');

  let canEdit: boolean;
  let canDelete: boolean;

  if (hasAll) {
    canEdit =
      hasModuleEditPermission(perms, 'To-Do - All') ||
      hasModuleEditPermission(perms, 'To-Do - My');
    canDelete =
      hasModuleDeletePermission(perms, 'To-Do - All') ||
      hasModuleDeletePermission(perms, 'To-Do - My');
  } else {
    canEdit   = hasModuleEditPermission(perms, 'To-Do - My');
    canDelete = hasModuleDeletePermission(perms, 'To-Do - My');
  }

  const canComplete =
    hasModuleExecutePermission(perms, 'To-Do - All') ||
    hasModuleExecutePermission(perms, 'To-Do - My');

  return {
    canView: hasAll || hasMy,
    canViewMy: hasMy,
    canViewAll: hasAll,
    canEdit,
    canDelete,
    canComplete,
  };
}
